import type { BaseComponentProps } from "@json-render/react";
import { type ReactNode, useMemo } from "react";
import ChatLinkableText from "@/components/ui/chat/ChatLinkableText";
import { parseTableCellLink } from "@/lib/utils/aiTableUtils";

type CatalogComponent = (ctx: BaseComponentProps) => ReactNode;

const toLinkableNode = (value: unknown): ReactNode => {
  if (parseTableCellLink(value) == null) return value as ReactNode;
  return <ChatLinkableText value={value} />;
};

/**
 * Replaces link-like values at `path` with {@link ChatLinkableText} nodes.
 * Supports `field` and `array[].field` paths.
 */
const applyLinkableTransformAtPath = (
  props: Record<string, unknown>,
  path: string,
): void => {
  const arraySegment = "[].";
  if (path.includes(arraySegment)) {
    const segmentIndex = path.indexOf(arraySegment);
    const arrayKey = path.slice(0, segmentIndex);
    const fieldKey = path.slice(segmentIndex + arraySegment.length);
    const arr = props[arrayKey];
    if (!Array.isArray(arr)) return;
    props[arrayKey] = arr.map((item) => {
      if (typeof item !== "object" || item === null) return item;
      const record = { ...(item as Record<string, unknown>) };
      if (fieldKey in record) {
        record[fieldKey] = toLinkableNode(record[fieldKey]);
      }
      return record;
    });
    return;
  }
  if (path in props) {
    props[path] = toLinkableNode(props[path]);
  }
};

/**
 * Returns a shallow clone of `props` with link-like values at `path` replaced by
 * {@link ChatLinkableText} nodes. Supports `field` and `array[].field` paths.
 */
export const patchLinkableStringProps = (
  props: Record<string, unknown>,
  linkablePaths: readonly string[],
): Record<string, unknown> => {
  const next = { ...props };
  for (const path of linkablePaths) {
    applyLinkableTransformAtPath(next, path);
  }
  return next;
};

/**
 * Wraps a json-render `*FromProps` adapter so configured string fields render
 * as links when the value is markdown `[label](url)` or `{ text, url }`.
 */
export const withLinkableStringProps = <T extends CatalogComponent>(
  Component: T,
  linkablePaths: readonly string[],
): T => {
  const Wrapped = (ctx: BaseComponentProps) => {
    const patchedProps = useMemo(
      () =>
        patchLinkableStringProps(
          ctx.props as Record<string, unknown>,
          linkablePaths,
        ),
      [ctx.props],
    );

    return Component({ ...ctx, props: patchedProps });
  };

  Wrapped.displayName = `WithLinkableStringProps(${
    (Component as CatalogComponent & { displayName?: string; name?: string })
      .displayName ??
    (Component as CatalogComponent & { name?: string }).name ??
    "Component"
  })`;
  return Wrapped as unknown as T;
};
